import { useEffect, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { api } from '../../../api/client';

/**
 * The three Sample Login toolbar dialogs — Copy Samples, Copy Test, Add Site.
 *
 * Extracted from SampleLoginView (2026-09-23) so Sample Transfer's Sample Login
 * panel can offer the SAME dialogs instead of forking them (CLAUDE.md rule 8 —
 * one implementation per capability). Registration still owns the behaviour;
 * these components only took its markup with it.
 *
 * The contract is deliberately MINIMAL — `{ id, matrix }` per sample rather than
 * Registration's SrSample — because the two callers hold different row models
 * (Registration's draft vs the transfer grid's server rows) and neither should
 * have to adopt the other's.
 */

/** The little a sample dialog needs to know about a sample. */
export interface CopyTestSample {
  /** Display SampleID, e.g. 260922008-001 — also the value passed back. */
  id: string;
  /** Sample Matrix NAME; the copy-test target list is filtered to a match. */
  matrix: string;
}

/**
 * Copy Samples — asks how many clones of the marked sample to create.
 * The caller runs the copy (POST .../copy-samples); this only collects the count.
 */
export function CopySamplesDialog({ sampleId, onClose, onApply }: {
  sampleId: string;
  onClose: () => void;
  onApply: (count: number) => void;
}) {
  // Held as text so the field can be cleared and typed into (ALPACA parity, user
  // report 2026-10-03: "can't enter No of samples manually"). Parsed + clamped
  // to ≥ 1 on OK; the +/- steppers still work.
  const [count, setCount] = useState('1');
  const num = Math.max(1, parseInt(count, 10) || 1);
  return (
    <AppDialog open title="Copy Samples" icon="ti-copy" width={520} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={() => onApply(num)}>OK</button>
      </>}>
      <p style={{ fontSize: 12.5, marginTop: 0 }}>
        Clones the marked sample <b>{sampleId}</b> — every field, its tests and its
        bottles — the number of times below. New rows are added after it; edit names, locations
        and collection date &amp; time per sample afterwards.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
        <label style={{ fontSize: 12, fontWeight: 600 }}>No Of Samples</label>
        <span className="sr-numspin">
          <button type="button" onClick={() => setCount(String(Math.max(1, num - 1)))}>−</button>
          <input value={count} aria-label="Number of copies" inputMode="numeric"
            onChange={e => setCount(e.target.value.replace(/[^0-9]/g, ''))}
            onBlur={() => setCount(String(num))} />
          <button type="button" onClick={() => setCount(String(num + 1))}>＋</button>
        </span>
      </div>
    </AppDialog>
  );
}

/**
 * Copy Tests to Samples — pick a source sample and the targets to copy its test
 * set onto. The caller performs the copy.
 *
 * Legacy guard kept (SampleLoginCopyTestGroupTestViewController.SL_CopyTest_Execute
 * line ~163): targets are filtered to samples sharing the SOURCE's Sample Matrix —
 * a test set only makes sense on the same matrix.
 */
export function CopyTestDialog({ samples, fromId: initialFrom, onClose, onApply, onWarn }: {
  samples: CopyTestSample[];
  /** Sample the picker opens on — the marked row. */
  fromId: string;
  onClose: () => void;
  onApply: (fromId: string, targetIds: string[]) => void;
  onWarn: (m: string) => void;
}) {
  const [from, setFrom] = useState(initialFrom || samples[0]?.id || '');
  const [targets, setTargets] = useState<string[]>([]);
  const toggle = (id: string) =>
    setTargets(t => (t.includes(id) ? t.filter(x => x !== id) : [...t, id]));

  const fromMatrix = samples.find(s => s.id === from)?.matrix ?? '';
  const eligible = samples.filter(s => s.id !== from && s.matrix === fromMatrix);
  const excludedByMatrix = samples.length - 1 - eligible.length;

  return (
    <AppDialog open title="Copy Tests to Samples" icon="ti-copy" width={520} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={() => {
          if (targets.length === 0) { onWarn('Tick at least one target sample.'); return; }
          onApply(from, targets);
        }}>OK</button>
      </>}>
      <div className="sr-fgrid" style={{ gridTemplateColumns: '110px minmax(0,1fr)', marginBottom: 10 }}>
        <label>Copy from</label>
        <select value={from} onChange={e => { setFrom(e.target.value); setTargets([]); }}>
          {samples.map(s => <option key={s.id}>{s.id}</option>)}
        </select>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 5 }}>Copy to:</div>
      <div style={{ border: '1px solid var(--ai-line)', borderRadius: 5, padding: '8px 12px', maxHeight: 180, overflow: 'auto' }}>
        {eligible.length === 0 && (
          <div style={{ color: 'var(--ai-muted)', fontStyle: 'italic' }}>
            No other sample shares {from}'s Sample Matrix.
          </div>
        )}
        {eligible.map(s => (
          <label key={s.id} style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 12.5, padding: '2px 0' }}>
            <input type="checkbox" checked={targets.includes(s.id)} onChange={() => toggle(s.id)} />
            {s.id}
          </label>
        ))}
      </div>
      {excludedByMatrix > 0 && (
        <div className="sr-hint-inline" style={{ marginTop: 6 }}>
          {excludedByMatrix} sample(s) hidden — different Sample Matrix than {from}.
        </div>
      )}
    </AppDialog>
  );
}

/** {id,name} option for a Sample Site FK dropdown. */
interface SiteOpt { id: string; name: string }
/** Client grid-lookup row — cols = [Address, Phone]. */
interface ClientOpt { id: string; name: string; cols?: string[] }

/** Add Site — the FULL legacy "Sample Sites" detail form (ALPACA
 *  SampleSites_DetailView, opened as a popup from Sample Registration →
 *  Add Site). Creates a complete dbo.SampleSites row via the shared
 *  /api/v1/sample-sites endpoint (same backend as Settings → Utility →
 *  Sample Sites) and hands the new {oid, name} back for the SiteName dropdown.
 *  Client + Address prefill from the job's client (legacy dependent fill);
 *  Site ID is server-generated ("SS#####"). Previously this was a minimal
 *  Site Name + Address popup (user request 2026-10-10: match ALPACA fields). */
export function AddSiteDialog({ onClose, onAdd, onWarn, clientOid }: {
  onClose: () => void;
  onAdd: (site: { oid: string; name: string }) => void;
  onWarn: (m: string) => void;
  clientOid?: string;
}) {
  // Text fields
  const [client, setClient] = useState(clientOid ?? '');
  const [siteCode, setSiteCode] = useState('');
  const [address, setAddress] = useState('');
  const [siteName, setSiteName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [samplePointId, setSamplePointId] = useState('');
  const [samplingAddress, setSamplingAddress] = useState('');
  const [keyMap, setKeyMap] = useState('');
  const [serviceArea, setServiceArea] = useState('');
  const [samplePointType, setSamplePointType] = useState('');
  const [siteNameArchived, setSiteNameArchived] = useState('');
  const [monitoringRequirement, setMonitoringRequirement] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [facilityName, setFacilityName] = useState('');
  const [facilityType, setFacilityType] = useState('');
  const [city, setCity] = useState('');
  const [stateField, setStateField] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [description, setDescription] = useState('');
  // FK dropdowns (Oid or '' = N/A)
  const [sampleType, setSampleType] = useState('');
  const [waterType, setWaterType] = useState('');
  const [systemType, setSystemType] = useState('');
  const [pwsSystemName, setPwsSystemName] = useState('');
  const [pwsId, setPwsId] = useState('');
  // Booleans
  const [isActive, setIsActive] = useState(true);
  const [blended, setBlended] = useState(false);
  // Option lists
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [sampleTypes, setSampleTypes] = useState<SiteOpt[]>([]);
  const [waterTypes, setWaterTypes] = useState<SiteOpt[]>([]);
  const [systemTypes, setSystemTypes] = useState<SiteOpt[]>([]);
  const [pwsNames, setPwsNames] = useState<SiteOpt[]>([]);
  const [pwsIds, setPwsIds] = useState<SiteOpt[]>([]);
  const [busy, setBusy] = useState(false);

  // Load every FK option list once. Prefill Address from the job's client, the
  // same dependent-field fill ALPACA does when it opens the Add Site popup.
  useEffect(() => {
    let alive = true;
    const pick = (r: { data?: { data?: unknown } }) => (r.data?.data ?? []) as SiteOpt[];
    void (async () => {
      try {
        const q = clientOid ? `?ids=${clientOid}` : '';
        const [cl, st, wt, sy, pn, pi] = await Promise.all([
          api.get(`/api/v1/sample-sites/clients${q}`),
          api.get('/api/v1/sample-sites/sample-types'),
          api.get('/api/v1/sample-sites/water-types'),
          api.get('/api/v1/sample-sites/system-types'),
          api.get('/api/v1/sample-sites/pws-names'),
          api.get('/api/v1/sample-sites/pws-ids'),
        ]);
        if (!alive) return;
        const clientRows = (cl.data?.data ?? []) as ClientOpt[];
        setClients(clientRows);
        setSampleTypes(pick(st)); setWaterTypes(pick(wt)); setSystemTypes(pick(sy));
        setPwsNames(pick(pn)); setPwsIds(pick(pi));
        if (clientOid) {
          const addr = clientRows.find(r => r.id.toLowerCase() === clientOid.toLowerCase())?.cols?.[0];
          if (addr) setAddress(a => a || addr);
        }
      } catch { /* option load failed → fields just render empty */ }
    })();
    return () => { alive = false; };
  }, [clientOid]);

  // Selecting a Client refills Address from that client (legacy dependent fill).
  const onClientChange = (oid: string) => {
    setClient(oid);
    const addr = clients.find(r => r.id === oid)?.cols?.[0];
    if (addr) setAddress(addr);
  };

  const save = async () => {
    if (!siteName.trim()) { onWarn('Enter the Site Name.'); return; }
    setBusy(true);
    const t = (s: string) => s.trim() || null;
    try {
      const res = await api.post<{ success: boolean; data: { id: string } }>(
        '/api/v1/sample-sites',
        {
          client: client || null, siteName: siteName.trim(), address: t(address),
          siteCode: t(siteCode), projectName: t(projectName), samplePointId: t(samplePointId),
          samplingAddress: t(samplingAddress), keyMap: t(keyMap), serviceArea: t(serviceArea),
          samplePointType: t(samplePointType), siteNameArchived: t(siteNameArchived),
          monitoringRequirement: t(monitoringRequirement), facilityId: t(facilityId),
          facilityName: t(facilityName), facilityType: t(facilityType), city: t(city),
          state: t(stateField), zipCode: t(zipCode), latitude: t(latitude),
          longitude: t(longitude), description: t(description),
          sampleType: sampleType || null, waterType: waterType || null,
          systemType: systemType || null, pwsSystemName: pwsSystemName || null,
          pwsid: pwsId || null, isActive, blended,
        },
      );
      const id = res.data?.data?.id;
      if (res.data?.success && id) {
        onAdd({ oid: id, name: siteName.trim() });
      } else {
        onWarn('Failed to save the site.');
        setBusy(false);
      }
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      onWarn(msg || 'Failed to save the site.');
      setBusy(false);
    }
  };

  const colStyle = { gridTemplateColumns: '150px minmax(0,1fr)', alignContent: 'start' } as const;
  const sel = (value: string, set: (v: string) => void, opts: SiteOpt[]) => (
    <select value={value} onChange={e => set(e.target.value)}>
      <option value="">N/A</option>
      {opts.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  );

  return (
    <AppDialog open title="Sample Sites" icon="ti-map-pin" width={900} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'OK'}
        </button>
      </>}>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div className="sr-fgrid" style={{ ...colStyle, flex: '1 1 360px' }}>
          <label>Site ID</label>
          <input value="" placeholder="Autogenerated" readOnly disabled />
          <label>Site Code</label>
          <input value={siteCode} onChange={e => setSiteCode(e.target.value)} />
          <label>Client</label>
          <select value={client} onChange={e => onClientChange(e.target.value)}>
            <option value="">N/A</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <label>Address</label>
          <input value={address} onChange={e => setAddress(e.target.value)} />
          <label className="req">Site Name</label>
          <input value={siteName} className={siteName ? undefined : 'sr-req-empty'}
            onChange={e => setSiteName(e.target.value)} />
          <label>Project Name</label>
          <input value={projectName} onChange={e => setProjectName(e.target.value)} />
          <label>Sample Point ID</label>
          <input value={samplePointId} onChange={e => setSamplePointId(e.target.value)} />
          <label>PWS System Name</label>
          {sel(pwsSystemName, setPwsSystemName, pwsNames)}
          <label>Sampling Address</label>
          <input value={samplingAddress} onChange={e => setSamplingAddress(e.target.value)} />
          <label>System Type</label>
          {sel(systemType, setSystemType, systemTypes)}
          <label>PWSID</label>
          {sel(pwsId, setPwsId, pwsIds)}
          <label>Key Map</label>
          <input value={keyMap} onChange={e => setKeyMap(e.target.value)} />
          <label>Service Area</label>
          <input value={serviceArea} onChange={e => setServiceArea(e.target.value)} />
          <label>Sample Type</label>
          {sel(sampleType, setSampleType, sampleTypes)}
          <label>Sample Point Type</label>
          <input value={samplePointType} onChange={e => setSamplePointType(e.target.value)} />
        </div>
        <div className="sr-fgrid" style={{ ...colStyle, flex: '1 1 360px' }}>
          <label>Site Name Archived</label>
          <input value={siteNameArchived} onChange={e => setSiteNameArchived(e.target.value)} />
          <label>Monitoring Requirements</label>
          <input value={monitoringRequirement} onChange={e => setMonitoringRequirement(e.target.value)} />
          <label>Facility ID</label>
          <input value={facilityId} onChange={e => setFacilityId(e.target.value)} />
          <label>Facility Name</label>
          <input value={facilityName} onChange={e => setFacilityName(e.target.value)} />
          <label>Facility Type</label>
          <input value={facilityType} onChange={e => setFacilityType(e.target.value)} />
          <label>Water Type</label>
          {sel(waterType, setWaterType, waterTypes)}
          <label>City</label>
          <input value={city} onChange={e => setCity(e.target.value)} />
          <label>State</label>
          <input value={stateField} onChange={e => setStateField(e.target.value)} />
          <label>Zip Code</label>
          <input value={zipCode} onChange={e => setZipCode(e.target.value)} />
          <label>Latitude</label>
          <input value={latitude} onChange={e => setLatitude(e.target.value)} />
          <label>Longitude</label>
          <input value={longitude} onChange={e => setLongitude(e.target.value)} />
          <label>Description</label>
          <input value={description} onChange={e => setDescription(e.target.value)} />
          <label>Is Active</label>
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)}
            style={{ justifySelf: 'start' }} />
          <label>Blended</label>
          <input type="checkbox" checked={blended} onChange={e => setBlended(e.target.checked)}
            style={{ justifySelf: 'start' }} />
        </div>
      </div>
    </AppDialog>
  );
}
